import React from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import QuizOutlined from '@mui/icons-material/QuizOutlined';
import ShortText from '@mui/icons-material/ShortText';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import SwapVert from '@mui/icons-material/SwapVert';
import StyleOutlined from '@mui/icons-material/StyleOutlined';
import PollOutlined from '@mui/icons-material/PollOutlined';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import CheckCircle from '@mui/icons-material/CheckCircle';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslate } from 'react-admin';
import { BlockFrame } from './BlockFrame';
import { AttrTextField } from './AttrFields';

const Remove: React.FC<{ onClick: () => void; disabled?: boolean; label: string }> = ({ onClick, disabled, label }) => (
    <Tooltip title={label}>
        <span><IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}><DeleteOutline fontSize="small" /></IconButton></span>
    </Tooltip>
);

const Explanation: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const translate = useTranslate();
    return <AttrTextField size="small" multiline minRows={2} fullWidth label={translate('blockEditor.views.explanation')} helperText={translate('blockEditor.views.explanationHint')} value={value ?? ''} onValueChange={onChange} />;
};

/* ───────────────────────── Inline quiz ───────────────────────── */

interface Choice { text: string; isCorrect: boolean }

export const InlineQuizBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { question, multiSelect, explanation } = node.attrs as { question: string; multiSelect: boolean; explanation: string };
    const choices: Choice[] = Array.isArray(node.attrs.choices) ? (node.attrs.choices as Choice[]) : [];
    const setChoices = (next: Choice[]) => updateAttributes({ choices: next });
    const toggleCorrect = (i: number) =>
        setChoices(multiSelect ? choices.map((c, k) => (k === i ? { ...c, isCorrect: !c.isCorrect } : c)) : choices.map((c, k) => ({ ...c, isCorrect: k === i })));

    return (
        <BlockFrame
            type="inline-quiz-block"
            icon={QuizOutlined}
            title={translate(multiSelect ? 'blockEditor.views.quiz.multi' : 'blockEditor.views.quiz.single')}
            selected={selected}
            actions={<FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={!!multiSelect} onChange={(e) => updateAttributes({ multiSelect: e.target.checked })} />} label={<Typography variant="caption">{translate('blockEditor.views.quiz.multiSelect')}</Typography>} />}
        >
            <Stack spacing={1.5}>
                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.quiz.question')} value={question} onValueChange={(v) => updateAttributes({ question: v })} />
                <Stack spacing={1}>
                    {choices.map((choice, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                            <Tooltip title={translate(choice.isCorrect ? 'blockEditor.views.quiz.markedCorrect' : 'blockEditor.views.quiz.markCorrect')}>
                                <IconButton size="small" onClick={() => toggleCorrect(i)} aria-pressed={choice.isCorrect} aria-label={translate('blockEditor.views.quiz.markCorrect')} sx={{ color: choice.isCorrect ? '#2e8b57' : 'var(--oa-text-2)' }}>
                                    {choice.isCorrect ? <CheckCircle /> : <RadioButtonUnchecked />}
                                </IconButton>
                            </Tooltip>
                            <AttrTextField size="small" fullWidth value={choice.text} onValueChange={(v) => setChoices(choices.map((c, k) => (k === i ? { ...c, text: v } : c)))} placeholder={translate('blockEditor.views.quiz.option', { n: i + 1 })} />
                            <Remove onClick={() => setChoices(choices.filter((_, k) => k !== i))} disabled={choices.length <= 2} label={translate('blockEditor.views.quiz.removeOption')} />
                        </Stack>
                    ))}
                </Stack>
                <Box><Button size="small" startIcon={<Add />} onClick={() => setChoices([...choices, { text: '', isCorrect: false }])}>{translate('blockEditor.views.quiz.addOption')}</Button></Box>
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Fill in the blanks ───────────────────────── */

interface Blank { id: string; acceptedAnswers: string[]; caseSensitive?: boolean }

export const FillBlankBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { template, explanation } = node.attrs as { template: string; explanation: string };
    const blanks: Blank[] = Array.isArray(node.attrs.blanks) ? (node.attrs.blanks as Blank[]) : [];
    const templateBlanks = (template ?? '').match(/\{\{\w+\}\}/g) ?? [];

    const insertBlank = () => {
        const used = new Set(blanks.map((b) => b.id));
        let n = blanks.length + 1;
        while (used.has(`blank_${n}`)) n++;
        const id = `blank_${n}`;
        updateAttributes({ template: `${template ?? ''}{{${id}}}`, blanks: [...blanks, { id, acceptedAnswers: [''], caseSensitive: false }] });
    };
    const updateBlank = (i: number, patch: Partial<Blank>) => updateAttributes({ blanks: blanks.map((b, k) => (k === i ? { ...b, ...patch } : b)) });
    const removeBlank = (i: number) => updateAttributes({ template: (template ?? '').split(`{{${blanks[i].id}}}`).join('___'), blanks: blanks.filter((_, k) => k !== i) });

    return (
        <BlockFrame type="fill-blank-block" icon={ShortText} title={translate('blockEditor.types.fill_blank.label')} meta={translate('blockEditor.views.fillBlank.count', { smart_count: templateBlanks.length })} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={insertBlank}>{translate('blockEditor.views.fillBlank.insert')}</Button>}>
            <Stack spacing={1.5}>
                <AttrTextField size="small" multiline minRows={3} fullWidth label={translate('blockEditor.views.fillBlank.template')} helperText={translate('blockEditor.views.fillBlank.templateHint')} value={template ?? ''} onValueChange={(v) => updateAttributes({ template: v })} InputProps={{ sx: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.875rem' } }} />
                {blanks.length > 0 && (
                    <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.fillBlank.answers')}</Typography>
                        {blanks.map((blank, i) => (
                            <Stack key={blank.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                                <code className="oa-block-code-chip">{blank.id}</code>
                                <AttrTextField size="small" fullWidth value={blank.acceptedAnswers.join(', ')} onValueChange={(v) => updateBlank(i, { acceptedAnswers: v.split(',').map((a) => a.trim()) })} placeholder={translate('blockEditor.views.fillBlank.answersPlaceholder')} />
                                <FormControlLabel sx={{ mr: 0, whiteSpace: 'nowrap' }} control={<Switch size="small" checked={blank.caseSensitive ?? false} onChange={(e) => updateBlank(i, { caseSensitive: e.target.checked })} />} label={<Typography variant="caption">{translate('blockEditor.views.fillBlank.caseSensitive')}</Typography>} />
                                <Remove onClick={() => removeBlank(i)} label={translate('blockEditor.views.fillBlank.remove')} />
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Matching ───────────────────────── */

interface Pair { left: string; right: string }

export const MatchingBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const pairs: Pair[] = Array.isArray(node.attrs.pairs) ? (node.attrs.pairs as Pair[]) : [];
    const explanation = (node.attrs.explanation as string) ?? '';
    const setPairs = (next: Pair[]) => updateAttributes({ pairs: next });

    return (
        <BlockFrame type="matching-block" icon={SwapHoriz} title={translate('blockEditor.types.matching.label')} meta={translate('blockEditor.views.matching.count', { smart_count: pairs.length })} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={() => setPairs([...pairs, { left: '', right: '' }])}>{translate('blockEditor.views.matching.add')}</Button>}>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{translate('blockEditor.views.matching.left')}</Typography>
                    <Box sx={{ width: 24 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{translate('blockEditor.views.matching.right')}</Typography>
                    <Box sx={{ width: 34 }} />
                </Stack>
                {pairs.map((pair, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <AttrTextField size="small" fullWidth value={pair.left} onValueChange={(v) => setPairs(pairs.map((p, k) => (k === i ? { ...p, left: v } : p)))} placeholder={translate('blockEditor.views.matching.term', { n: i + 1 })} />
                        <SwapHoriz fontSize="small" sx={{ color: 'var(--oa-text-2)' }} />
                        <AttrTextField size="small" fullWidth value={pair.right} onValueChange={(v) => setPairs(pairs.map((p, k) => (k === i ? { ...p, right: v } : p)))} placeholder={translate('blockEditor.views.matching.match', { n: i + 1 })} />
                        <Remove onClick={() => setPairs(pairs.filter((_, k) => k !== i))} disabled={pairs.length <= 2} label={translate('blockEditor.views.matching.remove')} />
                    </Stack>
                ))}
                <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.matching.shuffleHint')}</Typography>
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Ordering ───────────────────────── */

interface OrderItem { text: string; correctPosition: number }

export const OrderingBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const items: OrderItem[] = Array.isArray(node.attrs.items) ? (node.attrs.items as OrderItem[]) : [];
    const explanation = (node.attrs.explanation as string) ?? '';
    const reindex = (list: OrderItem[]) => list.map((item, i) => ({ ...item, correctPosition: i }));
    const setItems = (next: OrderItem[]) => updateAttributes({ items: reindex(next) });
    const move = (from: number, to: number) => {
        if (to < 0 || to >= items.length) return;
        const next = [...items];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setItems(next);
    };

    return (
        <BlockFrame type="ordering-block" icon={SwapVert} title={translate('blockEditor.types.ordering.label')} meta={translate('blockEditor.views.ordering.hint')} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={() => setItems([...items, { text: '', correctPosition: items.length }])}>{translate('blockEditor.views.ordering.add')}</Button>}>
            <Stack spacing={1.5}>
                {items.map((item, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <span className="oa-block-index">{i + 1}</span>
                        <Stack>
                            <IconButton size="small" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={translate('blockEditor.actions.moveUp')} sx={{ p: 0.25 }}><ArrowUpward sx={{ fontSize: 16 }} /></IconButton>
                            <IconButton size="small" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} aria-label={translate('blockEditor.actions.moveDown')} sx={{ p: 0.25 }}><ArrowDownward sx={{ fontSize: 16 }} /></IconButton>
                        </Stack>
                        <AttrTextField size="small" fullWidth value={item.text} onValueChange={(v) => setItems(items.map((it, k) => (k === i ? { ...it, text: v } : it)))} placeholder={translate('blockEditor.views.ordering.step', { n: i + 1 })} />
                        <Remove onClick={() => setItems(items.filter((_, k) => k !== i))} disabled={items.length <= 2} label={translate('blockEditor.views.ordering.remove')} />
                    </Stack>
                ))}
                <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.ordering.shuffleHint')}</Typography>
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Flashcards ───────────────────────── */

interface Card { front: string; back: string }

export const FlashcardBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const cards: Card[] = Array.isArray(node.attrs.cards) ? (node.attrs.cards as Card[]) : [];
    const setCards = (next: Card[]) => updateAttributes({ cards: next });

    return (
        <BlockFrame type="flashcard-block" icon={StyleOutlined} title={translate('blockEditor.types.flashcard.label')} meta={translate('blockEditor.views.flashcard.count', { smart_count: cards.length })} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={() => setCards([...cards, { front: '', back: '' }])}>{translate('blockEditor.views.flashcard.add')}</Button>}>
            <Stack spacing={1.5}>
                {cards.map((card, i) => (
                    <Box key={i} className="oa-block-subcard">
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">{translate('blockEditor.views.flashcard.card', { n: i + 1 })}</Typography>
                            <Remove onClick={() => setCards(cards.filter((_, k) => k !== i))} disabled={cards.length <= 1} label={translate('blockEditor.views.flashcard.remove')} />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <AttrTextField size="small" multiline minRows={2} fullWidth label={translate('blockEditor.views.flashcard.front')} value={card.front} onValueChange={(v) => setCards(cards.map((c, k) => (k === i ? { ...c, front: v } : c)))} />
                            <AttrTextField size="small" multiline minRows={2} fullWidth label={translate('blockEditor.views.flashcard.back')} value={card.back} onValueChange={(v) => setCards(cards.map((c, k) => (k === i ? { ...c, back: v } : c)))} />
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Poll ───────────────────────── */

interface PollOption { text: string }

export const PollBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { question, multiSelect, showResults } = node.attrs as { question: string; multiSelect: boolean; showResults: boolean };
    const options: PollOption[] = Array.isArray(node.attrs.options) ? (node.attrs.options as PollOption[]) : [];
    const setOptions = (next: PollOption[]) => updateAttributes({ options: next });

    return (
        <BlockFrame type="poll-block" icon={PollOutlined} title={translate('blockEditor.types.poll.label')} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={() => setOptions([...options, { text: '' }])}>{translate('blockEditor.views.poll.addOption')}</Button>}>
            <Stack spacing={1.5}>
                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.poll.question')} value={question} onValueChange={(v) => updateAttributes({ question: v })} />
                {options.map((option, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <span className="oa-block-index">{String.fromCharCode(65 + i)}</span>
                        <AttrTextField size="small" fullWidth value={option.text} onValueChange={(v) => setOptions(options.map((o, k) => (k === i ? { text: v } : o)))} placeholder={translate('blockEditor.views.poll.option', { n: String.fromCharCode(65 + i) })} />
                        <Remove onClick={() => setOptions(options.filter((_, k) => k !== i))} disabled={options.length <= 2} label={translate('blockEditor.views.poll.removeOption')} />
                    </Stack>
                ))}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControlLabel control={<Switch size="small" checked={!!multiSelect} onChange={(e) => updateAttributes({ multiSelect: e.target.checked })} />} label={<Typography variant="body2">{translate('blockEditor.views.poll.multiSelect')}</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={showResults !== false} onChange={(e) => updateAttributes({ showResults: e.target.checked })} />} label={<Typography variant="body2">{translate('blockEditor.views.poll.showResults')}</Typography>} />
                </Stack>
            </Stack>
        </BlockFrame>
    );
};
